import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../../services/apiService";
import { coordinatorFilterToParams } from "../_components/dashboardCoordinators";
import type { CoordinatorFilterValue } from "../_components/dashboardCoordinators";
import { eventTypesToParams } from "../_components/eventTypes";
import type { EventType } from "../_components/eventTypes";
import { duplicatesToParams, quickViewToParams } from "../_components/quickViews";
import type { QuickView } from "../_components/quickViews";
import type { EventPresentationState } from "../../../utils/methods/eventPresentation";

/** Backend caps `limit` at 200 (events.service#getAll); a month rarely needs more than one page. */
const PAGE_SIZE = 200;
const MAX_PAGES = 5;

type StatusFilter = { value: string; subSort?: string };

export type CalendarQuery = {
  /** Visible grid window, inclusive, YYYY-MM-DD. */
  rangeStart: string;
  rangeEnd: string;
  /** Same display filters as the table (status buckets, coordinators, text search). */
  searchBy: StatusFilter[];
  coordinatorFilter: CoordinatorFilterValue;
  /** "Show:" type chips (drafts / new requests / estimates sent / approved). */
  eventTypes?: EventType[];
  /** Quick view chip (unconfirmed / upcoming N days / ongoing). */
  quickView?: QuickView;
  /** "Show duplicates" checkbox. */
  showDuplicates?: boolean;
  search: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CalendarEvent = Record<string, any> & EventPresentationState & { _id: string };

/**
 * Loads every event touching the visible calendar window with the dashboard's
 * display filters applied — independent of the table's paginated Redux slice,
 * so switching views never disturbs the table's page.
 */
export function useCalendarEvents(query: CalendarQuery, enabled: boolean) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const requestSeq = useRef(0);

  const { rangeStart, rangeEnd, searchBy, coordinatorFilter, eventTypes, quickView = null, showDuplicates = false, search } = query;
  const filters = searchBy.map((s) => s.value).join(",");
  const subSort = searchBy.map((s) => s.subSort ?? "").join(",");
  const coordinatorParams = coordinatorFilterToParams(coordinatorFilter);
  const typeParams = eventTypesToParams(eventTypes);
  const quickParams = quickViewToParams(quickView);
  // Stable key so the effect only re-runs when something the API sees changes.
  const paramsKey = JSON.stringify({
    rangeStart,
    rangeEnd,
    filters,
    subSort,
    search,
    ...coordinatorParams,
    ...typeParams,
    ...quickParams,
    ...duplicatesToParams(showDuplicates),
  });

  useEffect(() => {
    if (!enabled) return;
    const seq = ++requestSeq.current;
    const baseParams = JSON.parse(paramsKey) as Record<string, string>;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      const all: CalendarEvent[] = [];
      try {
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const { response, error: reqError } = await apiRequest({
            method: "GET",
            url: "getAllEvents",
            params: {
              page,
              limit: PAGE_SIZE,
              filters: baseParams.filters,
              subSort: baseParams.subSort,
              search: baseParams.search,
              coordinatorIds: baseParams.coordinatorIds,
              coordinatorMatch: baseParams.coordinatorMatch,
              lifecycle: baseParams.lifecycle,
              quickView: baseParams.quickView,
              quickDays: baseParams.quickDays,
              overlapStart: baseParams.rangeStart,
              overlapEnd: baseParams.rangeEnd,
            },
            data: null,
            isToken: true,
          });
          if (cancelled || seq !== requestSeq.current) return;
          if (reqError || response?.status !== 200) {
            throw new Error(
              reqError?.response?.data?.message || "Could not load events",
            );
          }
          const rows = response?.data?.data;
          if (Array.isArray(rows)) all.push(...rows);
          const totalPages =
            Number(response?.data?.pagination?.totalPages) || 1;
          if (page >= totalPages) break;
        }
        setEvents(all);
      } catch (e) {
        if (cancelled || seq !== requestSeq.current) return;
        setError(e instanceof Error ? e.message : "Could not load events");
      } finally {
        if (!cancelled && seq === requestSeq.current) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, paramsKey, reloadTick]);

  const refetch = useCallback(() => setReloadTick((t) => t + 1), []);

  return { events, loading, error, refetch };
}
